import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import DescriptionIcon from "@material-ui/icons/Description";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ImageIcon from "@material-ui/icons/Image";
import LanguageIcon from "@material-ui/icons/Language";
import PhoneIcon from "@material-ui/icons/Phone";
import FileCopyIcon from "@material-ui/icons/FileCopyOutlined";
import ReplyIcon from "@material-ui/icons/Reply";
import VideocamIcon from "@material-ui/icons/Videocam";

import MarkdownWrapper from "../MarkdownWrapper";

const useStyles = makeStyles((theme) => {
  const dark = theme.mode === "dark";

  return {
    wrapper: {
      padding: theme.spacing(2),
      borderRadius: 8,
      backgroundColor: dark ? "#0b141a" : "#efeae2",
      // Trama discreta do papel de parede do WhatsApp
      backgroundImage: dark
        ? "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)",
      backgroundSize: "16px 16px",
      display: "flex",
      justifyContent: "flex-end",
    },
    bubble: {
      position: "relative",
      maxWidth: "85%",
      minWidth: 140,
      padding: theme.spacing(0.75, 1),
      borderRadius: 8,
      borderTopRightRadius: 0,
      backgroundColor: dark ? "#005c4b" : "#d9fdd3",
      color: dark ? "#e9edef" : "#111b21",
      boxShadow: "0 1px 0.5px rgba(11,20,26,.13)",
      fontSize: "0.875rem",
      lineHeight: 1.4,
      wordBreak: "break-word",
    },
    // No chat a bolha já vem do MessagesList: aqui só entra o conteúdo
    inline: {
      fontSize: "inherit",
      lineHeight: "inherit",
      wordBreak: "break-word",
    },
    header: {
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      whiteSpace: "pre-wrap",
    },
    media: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      height: 120,
      marginBottom: theme.spacing(0.5),
      borderRadius: 6,
      backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      color: dark ? "rgba(233,237,239,0.7)" : "rgba(17,27,33,0.55)",
      fontSize: "0.8rem",
      overflow: "hidden",
    },
    mediaImage: {
      width: "100%",
      maxHeight: 220,
      objectFit: "cover",
      borderRadius: 6,
      marginBottom: theme.spacing(0.5),
    },
    body: {
      whiteSpace: "pre-wrap",
    },
    footer: {
      marginTop: theme.spacing(0.5),
      fontSize: "0.75rem",
      opacity: 0.6,
      whiteSpace: "pre-wrap",
    },
    meta: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 3,
      marginTop: 2,
      fontSize: "0.6875rem",
      opacity: 0.6,
    },
    metaIcon: {
      fontSize: 15,
      color: "#53bdeb",
    },
    buttons: {
      marginTop: theme.spacing(0.5),
      borderTop: `1px solid ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}`,
    },
    button: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: theme.spacing(0.75, 0),
      color: "#53bdeb",
      fontSize: "0.85rem",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
      "&:last-child": {
        borderBottom: "none",
      },
    },
    buttonIcon: {
      fontSize: 16,
    },
    placeholder: {
      opacity: 0.55,
      fontStyle: "italic",
    },
  };
});

const buttonIcon = (type) => {
  switch (String(type || "").toUpperCase()) {
    case "URL":
      return LanguageIcon;
    case "PHONE_NUMBER":
      return PhoneIcon;
    case "COPY_CODE":
      return FileCopyIcon;
    default:
      return ReplyIcon;
  }
};

const mediaIcon = (format) => {
  switch (String(format || "").toUpperCase()) {
    case "IMAGE":
      return ImageIcon;
    case "VIDEO":
      return VideocamIcon;
    default:
      return DescriptionIcon;
  }
};

const mediaLabel = (format) => {
  switch (String(format || "").toUpperCase()) {
    case "IMAGE":
      return "Imagem";
    case "VIDEO":
      return "Vídeo";
    default:
      return "Documento";
  }
};

const isImageUrl = (url) => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url || "");
const isVideoUrl = (url) => /\.(mp4|webm|ogg)(\?|$)/i.test(url || "");

/**
 * Desenha o template como o cliente vê no WhatsApp — cabeçalho, corpo, rodapé
 * e botões, nessa ordem. Usado na tela de envio (variant "preview", com bolha e
 * fundo de chat) e na lista de mensagens (variant "message", só o conteúdo,
 * porque a bolha já é a do chat).
 */
const TemplatePreview = ({ snapshot, variant = "preview" }) => {
  const classes = useStyles();

  if (!snapshot) return null;

  const { header, body, footer, buttons = [] } = snapshot;
  const isMessage = variant === "message";

  const headerIsMedia = header && header.format !== "TEXT";
  const mediaUrl = header?.mediaUrl;
  const MediaIcon = mediaIcon(header?.format);

  const hora = new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const conteudo = (
    <>
      {headerIsMedia &&
        (mediaUrl && isImageUrl(mediaUrl) ? (
          <img
            src={mediaUrl}
            alt={mediaLabel(header.format)}
            className={classes.mediaImage}
          />
        ) : mediaUrl && isVideoUrl(mediaUrl) ? (
          <video src={mediaUrl} controls className={classes.mediaImage} />
        ) : (
          <div className={classes.media}>
            <MediaIcon fontSize="small" />
            {mediaUrl ? mediaLabel(header.format) : `${mediaLabel(header.format)} do cabeçalho`}
          </div>
        ))}

      {!!header?.text && <div className={classes.header}>{header.text}</div>}

      <div className={classes.body}>
        {body ? (
          <MarkdownWrapper>{body}</MarkdownWrapper>
        ) : (
          <span className={classes.placeholder}>Sem corpo de mensagem</span>
        )}
      </div>

      {!!footer && <div className={classes.footer}>{footer}</div>}

      {!isMessage && (
        <div className={classes.meta}>
          {hora}
          <DoneAllIcon className={classes.metaIcon} />
        </div>
      )}

      {buttons.length > 0 && (
        <div className={classes.buttons}>
          {buttons.map((button, index) => {
            const Icon = buttonIcon(button?.type);
            return (
              <div key={index} className={classes.button}>
                <Icon className={classes.buttonIcon} />
                {button?.text}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (isMessage) return <div className={classes.inline}>{conteudo}</div>;

  return (
    <div className={classes.wrapper}>
      <div className={classes.bubble}>{conteudo}</div>
    </div>
  );
};

export default TemplatePreview;
